import {AccountId, BaseCurrencyCode, CardId, TransactionId} from '@raison/types';
import {RsnAmount, useBoolean} from '@raison/ui-kit';
import {absurd} from 'fp-ts/lib/function';
import {useState} from 'react';
import {useSelector} from 'react-redux';
import {SERVICE_UNAVAILABLE_MESSAGE, maxAwaitTime, maxTriesCount} from '~/config';
import {legacyModalsApi, modalsTogglerClassDispatcher} from '~/entities/dialogs';
import {useHttpClient} from '~/library/network-facade';
import {createPaymentIOS} from '~/processes/cards';
import {checkNativeStripeAvailability} from '~/processes/cards/api/stripeNative';
import {
    checkStripeOrder as checkStripeOrderRequest,
    createPayment as createPaymentRequest,
    loadStripeOnce,
} from '~/processes/fiat-operations';
import {useCardSettings} from "../../model/useCardSettings";
import {CardLimitsModalProps} from "../../ui/Topup/CardLimitModal/CardLimitModal";
import {OperableAccount, OperableCard} from '~/redux/finances/domain';
import {updatePaymentMethod} from '~/redux/payments/paymentActions';
import {useAppDispatch} from '~/shared/store';

const getErrorModalStatus = legacyModalsApi.select('generalModals', 'errorModal');
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export type UseTopupCardOptions = {
    onSuccess?: (orderId: TransactionId) => void;
};

export const useTopupCard = (account: OperableAccount, options?: UseTopupCardOptions) => {
    const dispatch = useAppDispatch();
    const http = useHttpClient();
    const isModalOpened = useSelector(getErrorModalStatus);
    const [isFormDisabled, setIsFormDisabled] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const cardsSettings = useCardSettings(account.currencyCode);
    const [isLimitsModalOpen, setIsLimitsModalOpen] = useBoolean(false);
    const [cardLimitType, setCardLimitType] = useState<'single' | 'daily' | 'monthly' | null>(null);

    const handleSubmit = (account: OperableAccount, card: OperableCard, amount: RsnAmount) => {
        const {maximalPerOperationTotal, maximalPerDayCurrent, maximalPerMonthCurrent} = cardsSettings;

        const amountAsBigNumber = RsnAmount.toBigNumber(amount);

        if (amountAsBigNumber.gt(maximalPerOperationTotal)) {
            setCardLimitType('single');
            setIsLimitsModalOpen.on();
            return;
        }
        if (amountAsBigNumber.gt(maximalPerDayCurrent)) {
            setCardLimitType('daily');
            setIsLimitsModalOpen.on();
            return;
        }
        if (amountAsBigNumber.gt(maximalPerMonthCurrent)) {
            setCardLimitType('monthly');
            setIsLimitsModalOpen.on();
            return;
        }

        setIsFormDisabled(true);
        topupCard().finally(() => {
            setIsFormDisabled(false);
        });

        async function topupCard() {
            const {status, json} = await createPaymentRequest(http, account.id, card.id, {
                amount: RsnAmount.toNumber(amount),
                callback_url: undefined,
            });

            if (status === 'Abort') {
                return;
            }

            if (status === 'Crash') {
                setError(SERVICE_UNAVAILABLE_MESSAGE);
                dispatch(modalsTogglerClassDispatcher('generalModals')('errorModal'));
                return;
            }

            if (status === 400) {
                const amountError = json.fields?.amount?.[0];
                const callback_urlError = json.fields?.callback_url?.[0];

                setError(amountError || callback_urlError || SERVICE_UNAVAILABLE_MESSAGE);
                dispatch(modalsTogglerClassDispatcher('generalModals')('errorModal'));
                return;
            }

            const {client_secret, payment_method, payment_intent} = json;

            if (checkNativeStripeAvailability()) {
                try {
                    await createPaymentIOS(client_secret, payment_method);
                    await waitForOrder(payment_intent, account.id, card.id, account.currencyCode);
                    return;
                } catch (err) {
                    if (typeof err !== 'string') {
                        throw err;
                    }
                    if (err === 'canceled') {
                        return;
                    }
                    setError(err || SERVICE_UNAVAILABLE_MESSAGE);
                    dispatch(modalsTogglerClassDispatcher('generalModals')('errorModal'));
                    return;
                }
            }

            const stripe = await loadStripeOnce();

            if (!stripe) {
                throw new Error('Stripe instance is undefined');
            }

            const result = await stripe.confirmCardPayment(client_secret, {payment_method});

            if (result.error) {
                setError(result.error?.message ?? SERVICE_UNAVAILABLE_MESSAGE);
                dispatch(modalsTogglerClassDispatcher('generalModals')('errorModal'));
                return;
            }

            if (result.paymentIntent.status === 'succeeded' || result.paymentIntent.status === 'requires_capture') {
                await waitForOrder(result.paymentIntent.id, account.id, card.id, account.currencyCode);
            }
        }

        async function waitForOrder(
            externalId: string | number,
            accountId: AccountId,
            cardId: CardId,
            currencyCode: BaseCurrencyCode
        ) {
            let tries = maxTriesCount;
            let response = await checkStripeOrderRequest(http, accountId, cardId, externalId);
            while (response.status !== 200 && tries > 0) {
                tries -= 1;
                await sleep(maxAwaitTime);
                response = await checkStripeOrderRequest(http, accountId, cardId, externalId);
            }

            if (response.status === 'Abort') {
                throw response.reason;
            }

            if (response.status === 'Crash') {
                showGenericError();
                throw response.reason;
            }
            if (response.status === 204) {
                showGenericError();
                return;
            }

            if (response.status === 200) {
                dispatch(
                    updatePaymentMethod({
                        type: 'card',
                        id: cardId,
                        actionType: 'topup',
                        currency: currencyCode,
                    })
                );
                options?.onSuccess?.(response.json.id as TransactionId);

                return;
            }
            absurd(response);
        }

        function showGenericError() {
            if (!isModalOpened) {
                setError(SERVICE_UNAVAILABLE_MESSAGE);
                dispatch(modalsTogglerClassDispatcher('generalModals')('errorModal'));
            }
        }
    };

    const canSubmit = !isFormDisabled;

    const limitsOptions: CardLimitsModalProps = {
        account,
        type: cardLimitType,
        maximalPerOperationTotal: cardsSettings?.maximalPerOperationTotal,
        maximalPerDayTotal: cardsSettings?.maximalPerDayTotal,
        maximalPerDayCurrent: cardsSettings?.maximalPerDayCurrent,
        maximalPerMonthTotal: cardsSettings?.maximalPerMonthTotal,
        maximalPerMonthCurrent: cardsSettings?.maximalPerMonthCurrent,
    };

    return {handleSubmit, limitsOptions, canSubmit, error, isLimitsModalOpen, setIsLimitsModalOpen};
};
